/**
 * MarketingActivityLog — /log/marketing
 * Daily marketing activity log.
 * Mi6 palette: black #0B0B0F · red #DC2626.
 *
 * 2026-05-20 persistence fix: saves to Supabase via /api/me/activity/daily (PUT).
 * Marketing fields are packed into the notes column as [marketing:{json}] supplement.
 * emailsSent maps to the emails_sent column for server-side aggregation.
 * localStorage retained as offline cache.
 */
import { useState, useEffect } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { api, getSession } from "@/lib/session";
import { useToast } from "@/hooks/use-toast";

const MI6_BLACK = "#0B0B0F";
const MI6_RED  = "#DC2626";

const TODAY = new Date().toISOString().split("T")[0];

interface DailyLog {
  date: string;
  emailsSent: number;
  socialPostsPublished: number;
  webinarRegistrationsDriven: number;
  mqlsAdded: number;
  contentPiecesShipped: number;
  adSpend: number;
  notes: string;
}

function emptyLog(date: string): DailyLog {
  return { date, emailsSent: 0, socialPostsPublished: 0, webinarRegistrationsDriven: 0, mqlsAdded: 0, contentPiecesShipped: 0, adSpend: 0, notes: "" };
}

function buildKey(userId: string, date: string) {
  return `aip:activity-log:marketing:${userId}:${date}`;
}

function loadLogFromCache(userId: string, date: string): DailyLog | null {
  try {
    const raw = localStorage.getItem(buildKey(userId, date));
    // Legacy key (pre-fix, hardcoded marketing-user)
    const rawLegacy = localStorage.getItem(`aip:activity-log:marketing:marketing-user:${date}`);
    const data = raw ?? rawLegacy;
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

function saveLogToCache(userId: string, log: DailyLog): void {
  try { localStorage.setItem(buildKey(userId, log.date), JSON.stringify(log)); } catch { /* quota */ }
}

/** Parse a server row back into DailyLog. The marketing fields are stored in notes as [marketing:{json}]. */
function rowToDailyLog(row: any, date: string): DailyLog {
  const log = emptyLog(date);
  log.emailsSent = Number(row.emails_sent ?? 0);
  log.date = row.activity_date ?? date;
  // Parse supplemental marketing fields from notes
  if (row.notes) {
    const match = row.notes.match(/\[marketing:(\{.*?\})\]/s);
    if (match) {
      try {
        const mkt = JSON.parse(match[1]);
        log.socialPostsPublished       = Number(mkt.socialPostsPublished       ?? 0);
        log.webinarRegistrationsDriven = Number(mkt.webinarRegistrationsDriven ?? 0);
        log.mqlsAdded                  = Number(mkt.mqlsAdded                  ?? 0);
        log.contentPiecesShipped       = Number(mkt.contentPiecesShipped       ?? 0);
        log.adSpend                    = Number(mkt.adSpend                    ?? 0);
        log.notes                      = mkt.notes ?? "";
      } catch { /* malformed supplement */ }
    }
  }
  return log;
}

/** Fetch the server row for a given date. Returns null if not found. */
async function loadLogFromApi(date: string): Promise<DailyLog | null> {
  try {
    const data = await api<{ row: any }>(`/api/me/activity/daily?date=${date}`);
    if (!data?.row) return null;
    return rowToDailyLog(data.row, date);
  } catch {
    return null;
  }
}

function NumField({ label, value, onChange, prefix }: { label: string; value: number; onChange: (v: number) => void; prefix?: string }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">{label}</label>
      <div className="relative flex items-center">
        {prefix && <span className="absolute left-3 text-neutral-500 text-sm pointer-events-none">{prefix}</span>}
        <input
          type="number"
          min={0}
          value={value || ""}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          className="w-full border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0B0F] text-neutral-800"
          style={{ paddingLeft: prefix ? "28px" : "12px", paddingRight: "12px", paddingTop: "8px", paddingBottom: "8px" }}
          placeholder="0"
        />
      </div>
    </div>
  );
}

export default function MarketingActivityLog() {
  const { toast } = useToast();
  const session = getSession();
  const userId = session?.id ?? "marketing-user";

  const [date, setDate] = useState(TODAY);
  const [log, setLog] = useState<DailyLog>(emptyLog(TODAY));
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load log: show cache immediately, then hydrate from API.
  useEffect(() => {
    const cached = loadLogFromCache(userId, date);
    if (cached) {
      setLog(cached);
      setSaved(false);
    } else {
      setLog(emptyLog(date));
      setSaved(false);
    }
    // Hydrate from server
    loadLogFromApi(date).then((apiLog) => {
      if (apiLog) {
        setLog(apiLog);
        saveLogToCache(userId, apiLog);
      }
    }).catch(() => { /* offline — cache shown */ });
  }, [date, userId]);

  function update(field: keyof DailyLog, value: number | string) {
    setLog((l) => ({ ...l, [field]: value }));
    setSaved(false);
  }

  async function handleSave() {
    if (!session?.id) {
      toast({
        title: "Session unavailable",
        description: "Your session could not be verified. Please reload and try again.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);

    // Pack marketing-specific fields into the notes supplement.
    const supplement = {
      socialPostsPublished:       log.socialPostsPublished,
      webinarRegistrationsDriven: log.webinarRegistrationsDriven,
      mqlsAdded:                  log.mqlsAdded,
      contentPiecesShipped:       log.contentPiecesShipped,
      adSpend:                    log.adSpend,
      notes:                      log.notes,
    };
    const serverNotes = `[marketing:${JSON.stringify(supplement)}]`;

    try {
      await api("/api/me/activity/daily", {
        method: "PUT",
        body: JSON.stringify({
          activity_date: date,
          emails_sent:   log.emailsSent,
          source:        "self",
          notes:         serverNotes,
        }),
      });

      // Cache locally after successful server write.
      saveLogToCache(userId, { ...log, date });
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
    } catch (err: any) {
      // Fall back to local cache on API failure.
      saveLogToCache(userId, { ...log, date });
      const msg = err?.message ?? "Unknown error";
      console.error("[MarketingActivityLog] Supabase write failed:", msg);
      toast({
        title: "Save failed",
        description: "Your activity was saved locally. It will sync when the connection is restored.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }

  // Past 7 days logs for trend mini-view (from cache — API hydration fills these over time)
  const past7: DailyLog[] = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    const ds = d.toISOString().split("T")[0];
    return loadLogFromCache(userId, ds) ?? emptyLog(ds);
  });

  return (
    <OperatorShell breadcrumbs={[{ label: "Marketing Activity Log" }]} pageTitle="Marketing Activity Log" pageMeta="Daily activity log">

      {/* Header */}
      <div className="sticky top-14 z-30 bg-white border-b border-neutral-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[9px] uppercase tracking-[0.24em] text-neutral-500 mb-0.5">Daily Marketing Log</div>
            <h1 className="text-lg font-bold text-neutral-900">Marketing Activity Log</h1>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0B0F]"
            />
            {saved && (
              <span className="text-[11px] text-emerald-600 font-semibold bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
                Saved
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-60"
              style={{ background: MI6_RED }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 flex-1 w-full">

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

          {/* Activity metrics */}
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-5" style={{ borderLeft: `4px solid ${MI6_BLACK}` }}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 font-bold mb-4">Daily Activity — {new Date(date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
            <div className="space-y-3">
              <NumField label="Emails Sent" value={log.emailsSent} onChange={(v) => update("emailsSent", v)} />
              <NumField label="Social Posts Published" value={log.socialPostsPublished} onChange={(v) => update("socialPostsPublished", v)} />
              <NumField label="Webinar Registrations Driven" value={log.webinarRegistrationsDriven} onChange={(v) => update("webinarRegistrationsDriven", v)} />
              <NumField label="MQLs Added" value={log.mqlsAdded} onChange={(v) => update("mqlsAdded", v)} />
              <NumField label="Content Pieces Shipped" value={log.contentPiecesShipped} onChange={(v) => update("contentPiecesShipped", v)} />
              <NumField label="Ad Spend ($)" value={log.adSpend} onChange={(v) => update("adSpend", v)} prefix="$" />
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 font-bold mb-4">Notes</div>
            <textarea
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0B0F] resize-none"
              rows={10}
              value={log.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="What campaigns launched? What performed well? What's stalled? Anything blocking progress?"
            />
          </div>
        </div>

        {/* 7-day trend */}
        <div className="mt-5 bg-white rounded-xl border border-neutral-200 shadow-sm p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 font-bold mb-4">7-Day Activity Trend</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse" style={{ minWidth: "600px" }}>
              <thead>
                <tr className="border-b border-neutral-100">
                  <th className="text-left py-2 pr-3 text-[9px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Date</th>
                  <th className="text-center py-2 px-2 text-[9px] uppercase tracking-[0.12em] text-neutral-500 font-semibold">Emails</th>
                  <th className="text-center py-2 px-2 text-[9px] uppercase tracking-[0.12em] text-neutral-500 font-semibold">Social</th>
                  <th className="text-center py-2 px-2 text-[9px] uppercase tracking-[0.12em] text-neutral-500 font-semibold">Registrations</th>
                  <th className="text-center py-2 px-2 text-[9px] uppercase tracking-[0.12em] text-neutral-500 font-semibold">MQLs</th>
                  <th className="text-center py-2 px-2 text-[9px] uppercase tracking-[0.12em] text-neutral-500 font-semibold">Content</th>
                  <th className="text-center py-2 px-2 text-[9px] uppercase tracking-[0.12em] text-neutral-500 font-semibold">Ad Spend</th>
                </tr>
              </thead>
              <tbody>
                {past7.map((d, i) => {
                  const isToday = d.date === TODAY;
                  const displayDate = new Date(d.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
                  return (
                    <tr key={i} className="border-b border-neutral-50" style={{ background: isToday ? "#F0F4FF" : "white" }}>
                      <td className="py-2 pr-3 font-medium" style={{ color: isToday ? MI6_BLACK : "#6B6B70" }}>
                        {displayDate}{isToday ? " today" : ""}
                      </td>
                      <td className="py-2 px-2 text-center tabular-nums text-neutral-600">{d.emailsSent || "—"}</td>
                      <td className="py-2 px-2 text-center tabular-nums text-neutral-600">{d.socialPostsPublished || "—"}</td>
                      <td className="py-2 px-2 text-center tabular-nums text-neutral-600">{d.webinarRegistrationsDriven || "—"}</td>
                      <td className="py-2 px-2 text-center tabular-nums font-semibold" style={{ color: d.mqlsAdded > 0 ? MI6_BLACK : "#C9C9CC" }}>{d.mqlsAdded || "—"}</td>
                      <td className="py-2 px-2 text-center tabular-nums text-neutral-600">{d.contentPiecesShipped || "—"}</td>
                      <td className="py-2 px-2 text-center tabular-nums text-neutral-600">{d.adSpend ? `$${d.adSpend.toLocaleString()}` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick links */}
        <div className="mt-4 flex flex-wrap gap-3">
          <a href="#/marketing/calendar" className="text-xs text-neutral-500 hover:text-neutral-800 underline transition-colors">Back to Calendar</a>
          <a href="#/marketing/plan" className="text-xs text-neutral-500 hover:text-neutral-800 underline transition-colors">View Annual Plan</a>
          <a href="#/marketing/hooks" className="text-xs text-neutral-500 hover:text-neutral-800 underline transition-colors">Review Sales Hooks</a>
        </div>
      </main>
    </OperatorShell>
  );
}
